/**
 * Pure UTC-arithmetic helpers for building calendar grids. These operate on
 * "day identity" (year/month/day), never on a real instant — converting a day
 * cell to/from the workspace timezone happens once, at the call site, via
 * `lib/format`'s `toLocalInputValue` / `fromLocalInputValue`.
 */

export interface DayCell {
  key: string;
  year: number;
  month: number; // 1-12
  day: number;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function dayKey(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** The date-part of `toLocalInputValue`'s output ("2026-08-26T14:05" → "2026-08-26"). */
export function dayKeyFromLocalInput(localInputValue: string): string {
  return localInputValue.slice(0, 10);
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndexMon0(year: number, month: number, day: number): number {
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

export function addDaysToCell(year: number, month: number, day: number, delta: number): DayCell {
  const shifted = new Date(Date.UTC(year, month - 1, day + delta));
  return {
    key: dayKey(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate()),
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

export function startOfWeekMonday(year: number, month: number, day: number): DayCell {
  return addDaysToCell(year, month, day, -weekdayIndexMon0(year, month, day));
}

/** Six rows of seven — the grid never changes height between months. */
export function buildMonthGrid(year: number, month: number): Array<DayCell & { currentMonth: boolean }> {
  const leading = weekdayIndexMon0(year, month, 1);
  const total = daysInMonth(year, month);
  const cells: Array<DayCell & { currentMonth: boolean }> = [];

  const previous = addMonths(year, month, -1);
  const previousTotal = daysInMonth(previous.year, previous.month);
  for (let index = leading - 1; index >= 0; index -= 1) {
    const day = previousTotal - index;
    cells.push({
      key: dayKey(previous.year, previous.month, day),
      year: previous.year,
      month: previous.month,
      day,
      currentMonth: false,
    });
  }

  for (let day = 1; day <= total; day += 1) {
    cells.push({ key: dayKey(year, month, day), year, month, day, currentMonth: true });
  }

  const next = addMonths(year, month, 1);
  let day = 1;
  while (cells.length < 42) {
    cells.push({ key: dayKey(next.year, next.month, day), year: next.year, month: next.month, day, currentMonth: false });
    day += 1;
  }

  return cells;
}

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
